/**
 * @file OrgLevelsList.js
 * @description This file contains the OrgLevelsList component, which displays a list of organization levels with checkboxes and links.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getOrgLevelsForType,
  deleteOrgLevelForTypeAndID,
  updateOrgLevelForTypeAndID,
} from "../../services/orgLevelService";
import style from "../population-groups/PopulationGroup.module.scss";
import orgLevelsStyles from "./OrgLevelsList.module.scss";
import "../page-list/PageList.css";
import { SecurityAttributes } from "../../utils/data/enums";
import { useAuthContext } from "../../store/AuthContext";
import SearchComponent from "../helpers/search/SearchComponent";
import EmptyTableRow from "../ui/no_data_indicators/EmptyTableRow";
import Spinner from "../ui/spinners/Spinner";
import Toast from "../ui/toasts/Toast";
import PaginationComponent from "../helpers/PaginationComponent";
import { orgLevelNameFromType } from "./OrgLevelCommon";
import { getText } from '../../i18n';

const OrgLevelsList = () => {
  const navigate = useNavigate();
  let { orgLevelType } = useParams();
  const [allOrgLevels, setAllOrgLevels] = useState([]);
  const [displayOrgLevels, setDisplayOrgLevels] = useState([]);
  const { hasAttribute } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [orgLevelTypeName, setOrgLevelTypeName] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const SortDirections = {
    ASCENDING: "asc",
    DESCENDING: "desc",
  };

  const defaultSortOrder = {
    key: "name",
    direction: SortDirections.ASCENDING,
  };

  const [sortConfig, setSortConfig] = useState(defaultSortOrder);

  const loadOrgLevels = async () => {
    setIsLoading(true);
    getOrgLevelsForType(orgLevelType).then((response) => {
      setSearchTerm("");
      setAllOrgLevels(response);
      sortAndFilterData(response);
      setIsLoading(false);
    })
  };

  const handleCheckboxChange = (orgLevelId, isChecked) => {

    const orgLevels = allOrgLevels.map((orgLevel) => {
      if (orgLevel.id === orgLevelId) {
        return { ...orgLevel, isChecked };
      }
      return orgLevel;
    })

    setAllOrgLevels(orgLevels);
    sortAndFilterData(orgLevels);
  };

  const deleteSelected = async () => {
    let orgLevelsToDelete = allOrgLevels
      .filter((orgLevel) => orgLevel.isChecked);

    let remainingOrgLevels = allOrgLevels;

    orgLevelsToDelete.forEach(async (orgLevel) => {
      var response = await deleteOrgLevelForTypeAndID(orgLevelType, orgLevel.id);

      if (response.error) {
        Toast.error(response.message);
      } else if (response.status === 200) {
        Toast.success(`${orgLevel.code} deleted successfully`);
        remainingOrgLevels = remainingOrgLevels.filter((level) => level.id !== orgLevel.id)
        setAllOrgLevels(remainingOrgLevels);
        sortAndFilterData(remainingOrgLevels);
      } else {
        Toast.error(`${orgLevel.code} delete error`);
      }
    })
  };

  useEffect(() => {
    loadOrgLevels();
    var typeName = orgLevelNameFromType(orgLevelType);
    setOrgLevelTypeName(typeName);
  }, [orgLevelType]);

  useEffect(() => {
    if (currentPage !== 1)
      setCurrentPage(1);
    else
      sortAndFilterData(allOrgLevels);
  }, [sortConfig, searchTerm]);

  useEffect(() => {
    sortAndFilterData(allOrgLevels);
  }, [currentPage]);


  const sortAndFilterData = (orgLevels) => {
    var filteredOrgLevels = orgLevels;

    if (searchTerm) {
      var searchFor = searchTerm.toLocaleLowerCase();
      filteredOrgLevels = orgLevels.filter(level => level.name.toLowerCase().includes(searchFor) || level.code.toLowerCase().includes(searchFor))
    }

    const sortedData = filteredOrgLevels.sort((a, b) => {
      let aValue = a[sortConfig.key] || "";
      let bValue = b[sortConfig.key] || "";

      return sortConfig.direction === SortDirections.ASCENDING
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    const firstItem = (currentPage === 1) ? 0 : (currentPage - 1) * pageSize - 1;
    const pageOfData = sortedData.slice(firstItem, firstItem + pageSize);
    setDisplayOrgLevels(pageOfData)
    setTotalPages(Math.ceil(sortedData.length / pageSize));
  }

  const handleSort = (key) => {
    var sortBy = {
      key,
      direction: sortConfig.direction === SortDirections.ASCENDING ? SortDirections.DESCENDING : SortDirections.ASCENDING,
    };
    setSortConfig(sortBy);
  }

  const handleSearch = (searchBy) => {
    setSearchTerm(searchBy);
  };

  const toggleOrgLevelStatus = async (orgLevel) => {

    try {

      if (!orgLevel) throw new Error("Org Level Not Found");

      const updatedOrgLevel = { ...orgLevel, isActive: !orgLevel.isActive };

      const response = await updateOrgLevelForTypeAndID(orgLevelType, null, updatedOrgLevel);

      if (response.error) {
        Toast.error(response.message);
      } else if (response.status === 200) {
        Toast.success(`${updatedOrgLevel.isActive ? "Enabled" : "Disabled"} successfully`);
      } else {
        Toast.error(getText('msg_493c5c19f94f'));
      }

      await loadOrgLevels();
    } catch (error) {
      console.error("Failed to toggle org level status", error);
      Toast.error(getText('msg_493c5c19f94f'));
    }
  }

  return (
    <div className="page-content">
      <div className="card">
        <div className="card-body px-0 py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="float-start d-flex align-items-center">
              <p className="page-title card-title ms-4 me-5 mb-0">
                {orgLevelTypeName}
              </p>
              <div>
                <SearchComponent onSearch={(event) => handleSearch(event)} placeholder={`Search ${orgLevelTypeName}`} initialValue={searchTerm} />
              </div>
            </div>
            {hasAttribute(
              SecurityAttributes.OrganizationLevelsFullAccess
            ) && (
                <div className="float-end d-flex align-items-center">
                  <span
                    type="button"
                    onClick={deleteSelected}
                    className="ing-text-buttons mx-3"
                  >
                    <i className="bi bi-trash3 me-2" />
                    {getText('msg_f6fdbe48dc54')}
                  </span>
                  <div to="new-portal-page">
                    <span
                      onClick={() => {
                        navigate("new");
                      }}
                      type="button"
                      className="ms-3 me-4  button-base ing-button-primary-left "
                    >
                      <i className="bi bi-plus-lg me-1" />
                      {getText('msg_61cc55aa0453')}
                    </span>
                  </div>
                </div>
              )}
          </div>
          {isLoading ? (
            <div className="d-flex justify-content-center" style={{ "margin-top": "100px;" }}><Spinner width={50} /></div>
          ) : (
            <div className="table-responsive-xxl w-100">
              <table className={`table table-hover align-middle ${orgLevelsStyles.orgLevelsTable}`}>
                <colgroup>
                  <col className={orgLevelsStyles.colSelect} />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead className="custom-table-header table-light">
                  <tr>
                    <th className={`text-center ${style.checkboxColumnHead}`}>
                    </th>
                    <th className="columnHead table-column-header-text">
                      <span>{getText('msg_709a23220f2c')}</span>
                      <span onClick={() => handleSort("name")} style={{ cursor: "pointer" }} className='mx-2'>
                        <svg width={11} height={6} viewBox="0 0 11 6" fill="none">
                          <path transform={sortConfig.key === 'name' && sortConfig.direction === SortDirections.DESCENDING ? 'rotate(180 5.25 3)' : ''} d="M1 0.75L5.25 5.25L9.5 0.75" stroke="#2F2B43" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </th>
                    <th className="columnHead table-column-header-text">
                      <span>{getText('msg_adac69379a62')}</span>
                      <span onClick={() => handleSort("code")} style={{ cursor: "pointer" }} className='mx-2'>
                        <svg width={11} height={6} viewBox="0 0 11 6" fill="none">
                          <path transform={sortConfig.key === 'code' && sortConfig.direction === SortDirections.DESCENDING ? 'rotate(180 5.25 3)' : ''} d="M1 0.75L5.25 5.25L9.5 0.75" stroke="#2F2B43" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </th>
                    <th className="columnHead table-column-header-text">
                      {getText('msg_20063ad90532')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayOrgLevels.length === 0 && (
                    <EmptyTableRow
                      colSpan={4}
                      title={`No ${orgLevelTypeName} found`}
                      description={getText('msg_12987c465e21')}
                    />
                  )}
                  {displayOrgLevels.map((orgLevel) => (
                    <tr key={orgLevel.id}>
                      <td className={`text-center ${style.checkboxPadding}`}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={orgLevel.isChecked}
                          onChange={(e) =>
                            handleCheckboxChange(orgLevel.id, e.target.checked)
                          }
                        />
                      </td>
                      <td
                        onClick={() => {
                          navigate(`${orgLevel.id}`);
                        }}
                        className="titleCell"
                      >
                        {orgLevel.name}
                      </td>
                      <td
                        onClick={() => {
                          navigate(`${orgLevel.id}`);
                        }}
                        className="subtitleCell"
                      >
                        {orgLevel.code}
                      </td>
                      <td>
                        <div className="form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input custom-switch-input"
                            checked={orgLevel.isActive}
                            onChange={() => toggleOrgLevelStatus(orgLevel)}
                            disabled={hasAttribute(
                              SecurityAttributes.OrganizationLevelsReadOnly
                            )}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 0 && (
                <div className="mt-auto">
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgLevelsList;

